/**
 * Where an edit to an object is seen, and so where it is written: the page that
 * shows for a Pages container's shared content (`capturePageState`), else the
 * state that shows, else Base. The page comes first because its assigned state
 * is usually the one showing, and the page route forks a state two pages share
 * where the state route would edit both.
 */

import type { SceneObject } from '../../core/SceneObject';
import { pageCaptureContext, type PageCaptureContext } from '../states/pageCapture';
import { refuse } from './checks';
import type { AuthoringPorts, Refusal } from './types';

export type Route = { to: 'page'; page: PageCaptureContext } | { to: 'state'; state: string } | { to: 'base' };

/** The state an edit of this kind goes into on `object`, or undefined for Base. */
export type StateOf = (object: SceneObject) => string | undefined;

export function routeOf(object: SceneObject, ports: AuthoringPorts, stateOf: StateOf): Route {
    const page = ports.pageEditing ? pageCaptureContext(object, ports.pageEditing) : null;
    if (page) return { to: 'page', page };
    const state = stateOf(object);
    return state === undefined ? { to: 'base' } : { to: 'state', state };
}

/**
 * Why `edit` on shared content may not go into the state that shows: the
 * showing page assigns that state to the object or an ancestor under the
 * container, and page editing is off. That state is the page's, and may be
 * another page's too; writing into it as a group state would edit every page
 * that assigns it. For a pose there is a second hazard: keying it can wrap
 * the object in a rig the container then takes for a page.
 */
export function pageStateRefusal(object: SceneObject, ports: AuthoringPorts, stateOf: StateOf, edit: string): Refusal | null {
    const route = routeOf(object, ports, stateOf);
    if (route.to !== 'state') return null;
    const page = pageCaptureContext(object, () => true);
    if (!page) return null;
    const assigned = page.pages.getData().pages?.[page.pageIndex]?.states ?? [];
    for (let node: SceneObject | null = object; node && node !== page.container; node = node.getParent()) {
        const path = object.traceScene.getRelativePath(node, page.container);
        if (!assigned.some(a => a.target === path && a.state === route.state)) continue;
        return {
            ...refuse(
                'STATE_ROUTED',
                `"${object.name}" shows "${route.state}", the state the page "${page.pageName}" assigns, so ${edit} set here would change every page that assigns it; edit it with this page as the page-editing context, or show all pages to edit Base`,
                object,
            ),
            pageName: page.pageName,
        };
    }
    return null;
}

/**
 * {@link pageStateRefusal} for an edit hosts made before it had a page route: a
 * host that passes no `pageEditing` port keeps writing into the showing state,
 * as it did, and only one that passes the port is refused while page editing is
 * off. Pose, visibility, a text opacity and a slot's roughness, metallic and
 * opacity refuse without a port, as they always have.
 */
export function pageStateRefusalIfAsked(object: SceneObject, ports: AuthoringPorts, stateOf: StateOf, edit: string): Refusal | null {
    return ports.pageEditing ? pageStateRefusal(object, ports, stateOf, edit) : null;
}
